<template>
  <h2 class="text-primary mx-2">API Request Log</h2>
  <div class="container-fluid" v-if="isSystemAdmin">
    <div class="card mel-glass-panel mb-3">
      <div class="card-body">
        <div class="row g-2">
          <div class="col-12 col-md-2">
            <label class="form-label small mb-1">Method</label>
            <select class="form-select form-select-sm" v-model="filter.method">
              <option :value="null">(any)</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="PATCH">PATCH</option>
              <option value="DELETE">DELETE</option>
            </select>
          </div>
          <div class="col-12 col-md-4">
            <label class="form-label small mb-1">Path contains</label>
            <input type="text" class="form-control form-control-sm" v-model.trim="filter.path" placeholder="e.g. orders" />
          </div>
          <div class="col-6 col-md-2">
            <label class="form-label small mb-1">Status code</label>
            <input type="number" class="form-control form-control-sm" v-model.number="filter.statusCode" placeholder="exact" min="100" max="599" />
          </div>
          <div class="col-3 col-md-1">
            <label class="form-label small mb-1">Min</label>
            <input type="number" class="form-control form-control-sm" v-model.number="filter.minStatusCode" min="100" max="599" />
          </div>
          <div class="col-3 col-md-1">
            <label class="form-label small mb-1">Max</label>
            <input type="number" class="form-control form-control-sm" v-model.number="filter.maxStatusCode" min="100" max="599" />
          </div>
          <div class="col-12 col-md-3">
            <label class="form-label small mb-1">User</label>
            <div class="input-group input-group-sm">
              <TypeaheadComponent
                v-model="selectedUser"
                :items="availableUsers"
                :item-projection="userProjection"
                :placeholder="'Name or email'"
                :min-input-length="1"
                :max-items="20"
                :input-class="'form-control form-control-sm'"
                @update:modelValue="onUserSelected"
              />
              <button v-if="selectedUser" class="btn btn-outline-secondary" type="button" @click="clearUser" title="Clear user filter">
                <font-awesome-icon :icon="['fas', 'xmark']" />
              </button>
            </div>
          </div>
          <div class="col-12 col-md-3">
            <label class="form-label small mb-1">From</label>
            <input type="datetime-local" class="form-control form-control-sm" v-model="filter.from" />
          </div>
          <div class="col-12 col-md-3">
            <label class="form-label small mb-1">To</label>
            <input type="datetime-local" class="form-control form-control-sm" v-model="filter.to" />
          </div>
          <div class="col-6 col-md-2">
            <label class="form-label small mb-1">Min duration (ms)</label>
            <input type="number" class="form-control form-control-sm" v-model.number="filter.minResponseTimeMs" min="0" />
          </div>
          <div class="col-6 col-md-2">
            <label class="form-label small mb-1">Page size</label>
            <select class="form-select form-select-sm" v-model.number="take">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="200">200</option>
              <option :value="500">500</option>
            </select>
          </div>
          <div class="col-12 col-md-2 d-flex align-items-end gap-2">
            <button class="btn btn-primary btn-sm flex-grow-1" @click="applyFilter" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              <font-awesome-icon v-else :icon="['fas', 'magnifying-glass']" class="me-1" />
              Search
            </button>
            <button class="btn btn-outline-secondary btn-sm" @click="resetFilter" :disabled="loading" title="Reset filters">
              <font-awesome-icon :icon="['fas', 'filter-circle-xmark']" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div class="d-flex justify-content-between align-items-center mb-2">
      <div class="text-secondary small">
        <span v-if="page.total > 0">
          Showing {{ skip + 1 }}–{{ Math.min(skip + page.items.length, page.total) }} of {{ page.total }}
        </span>
        <span v-else-if="!loading">No results</span>
      </div>
      <div class="btn-group btn-group-sm">
        <button class="btn btn-outline-secondary" @click="prevPage" :disabled="loading || skip === 0">
          <font-awesome-icon :icon="['fas', 'chevron-left']" /> Prev
        </button>
        <button class="btn btn-outline-secondary" @click="nextPage" :disabled="loading || skip + take >= page.total">
          Next <font-awesome-icon :icon="['fas', 'chevron-right']" />
        </button>
      </div>
    </div>

    <div class="table-responsive">
      <table class="table table-hover table-sm align-middle">
        <thead>
          <tr>
            <th>When</th>
            <th>Method</th>
            <th>Path</th>
            <th class="text-end">Status</th>
            <th class="text-end">Time (ms)</th>
            <th>User</th>
            <th>IP</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in page.items" :key="entry.id"
              :class="rowClass(entry.responseStatusCode)"
              role="button"
              @click="select(entry.id)">
            <td><RelativeDateTime :at="entry.requestAt" /></td>
            <td>
              <span class="badge" :class="methodBadge(entry.method)">{{ entry.method }}</span>
            </td>
            <td class="text-break">
              <template v-for="parsed in [parseEntityPath(entry.path)]" :key="0">
                <template v-if="parsed">
                  <span class="text-secondary">{{ parsed.prefix }}</span><RouterLink
                    :to="parsed.to"
                    class="link-primary fw-semibold"
                    :class="{ 'pe-none opacity-50': navigatingPath === entry.path }"
                    :title="`Switch to entity's ${parsed.config.scope} and open`"
                    @click.prevent.stop="navigateToEntity(parsed, entry.path)"
                  >{{ parsed.id }}<span v-if="navigatingPath === entry.path" class="spinner-border spinner-border-sm ms-1"></span></RouterLink><span class="text-secondary">{{ parsed.rest }}</span>
                </template>
                <span v-else>{{ entry.path }}</span>
              </template>
              <span v-if="entry.queryString" class="text-secondary small">{{ entry.queryString }}</span>
              <span v-for="hint in [siteHintFromQuery(entry.queryString)]" :key="0">
                <span v-if="hint" class="badge text-bg-light text-secondary border ms-1" :title="`siteId=${hint.id}`">
                  <font-awesome-icon :icon="['far', 'warehouse']" class="me-1" />{{ hint.name }}
                </span>
              </span>
            </td>
            <td class="text-end">
              <span class="badge" :class="statusBadge(entry.responseStatusCode)">{{ entry.responseStatusCode }}</span>
            </td>
            <td class="text-end font-monospace">{{ entry.responseTimeMs }}</td>
            <td>
              <template v-if="entry.userId">
                <span>{{ userLabel(entry.userId) }}</span>
                <sup class="text-secondary ms-1">#{{ entry.userId }}</sup>
              </template>
              <template v-else>—</template>
            </td>
            <td class="font-monospace small">{{ entry.remoteIpAddress ?? '—' }}</td>
            <td class="text-end">
              <button class="btn btn-outline-secondary btn-sm" @click.stop="select(entry.id)" title="View detail">
                <font-awesome-icon :icon="['fas', 'eye']" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="loading && page.items.length === 0" class="text-center text-secondary py-4">
      <div class="spinner-border" role="status"></div>
    </div>

    <!-- Detail modal -->
    <div class="modal fade" id="apiRequestLogDetailModal" ref="detailModalRef" tabindex="-1" aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              Request
              <span v-if="detail" class="badge ms-2" :class="methodBadge(detail.method)">{{ detail.method }}</span>
              <code v-if="detail" class="ms-2">
                <template v-for="parsed in [parseEntityPath(detail.path)]" :key="0">
                  <template v-if="parsed">
                    <span>{{ parsed.prefix }}</span><a
                      href="#"
                      class="link-primary fw-semibold"
                      :class="{ 'pe-none opacity-50': navigatingPath === detail.path }"
                      :title="`Switch to entity's ${parsed.config.scope} and open`"
                      @click.prevent="navigateToEntity(parsed, detail.path)"
                    >{{ parsed.id }}<span v-if="navigatingPath === detail.path" class="spinner-border spinner-border-sm ms-1"></span></a><span>{{ parsed.rest }}</span>
                  </template>
                  <span v-else>{{ detail.path }}</span>
                </template>
              </code>
            </h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="detailLoading" class="text-center py-4">
              <div class="spinner-border" role="status"></div>
            </div>
            <div v-else-if="!detail" class="text-secondary">No data</div>
            <div v-else>
              <div class="row g-3 mb-3">
                <div class="col-md-6">
                  <dl class="row mb-0 small">
                    <dt class="col-5">Request at</dt>
                    <dd class="col-7">{{ detail.requestAt }}</dd>
                    <dt class="col-5">Response at</dt>
                    <dd class="col-7">{{ detail.responseAt }}</dd>
                    <dt class="col-5">Duration</dt>
                    <dd class="col-7">{{ detail.responseTimeMs }} ms</dd>
                    <dt class="col-5">Status</dt>
                    <dd class="col-7">
                      <span class="badge" :class="statusBadge(detail.responseStatusCode)">{{ detail.responseStatusCode }}</span>
                    </dd>
                  </dl>
                </div>
                <div class="col-md-6">
                  <dl class="row mb-0 small">
                    <dt class="col-5">User</dt>
                    <dd class="col-7">
                      <template v-if="detail.userId">
                        {{ userLabel(detail.userId) }}
                        <sup class="text-secondary">#{{ detail.userId }}</sup>
                      </template>
                      <template v-else>—</template>
                    </dd>
                    <dt class="col-5">IP</dt>
                    <dd class="col-7 font-monospace">{{ detail.remoteIpAddress ?? '—' }}</dd>
                    <dt class="col-5">User-Agent</dt>
                    <dd class="col-7 text-break">{{ detail.userAgent ?? '—' }}</dd>
                    <dt class="col-5">Content-Type</dt>
                    <dd class="col-7">{{ detail.contentType ?? '—' }}</dd>
                    <dt class="col-5">Content-Length</dt>
                    <dd class="col-7">{{ detail.contentLength ?? '—' }}</dd>
                  </dl>
                </div>
              </div>

              <div v-if="detail.queryString">
                <h6>
                  Query String
                  <span v-for="hint in [siteHintFromQuery(detail.queryString)]" :key="0">
                    <span v-if="hint" class="badge text-bg-light text-secondary border ms-2" :title="`siteId=${hint.id}`">
                      <font-awesome-icon :icon="['far', 'warehouse']" class="me-1" />{{ hint.name }}
                    </span>
                  </span>
                </h6>
                <pre class="bg-body-tertiary p-2 rounded small mb-3"><code>{{ detail.queryString }}</code></pre>
              </div>

              <h6>Request Headers</h6>
              <pre class="bg-body-tertiary p-2 rounded small mb-3"><code>{{ formatJson(detail.requestHeaders) }}</code></pre>

              <h6>Request Body</h6>
              <pre class="bg-body-tertiary p-2 rounded small mb-3"><code>{{ formatJson(detail.requestBody) }}</code></pre>

              <h6>Response Headers</h6>
              <pre class="bg-body-tertiary p-2 rounded small mb-3"><code>{{ formatJson(detail.responseHeaders) }}</code></pre>

              <h6>Response Body</h6>
              <pre class="bg-body-tertiary p-2 rounded small mb-0"><code>{{ formatJson(detail.responseBody) }}</code></pre>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="container-fluid" v-else>
    <div class="alert alert-danger" role="alert">
      <h4 class="alert-heading">
        <font-awesome-icon :icon="['fas', 'shield']" class="me-2" />
        {{ $t('error.pageAccessDenied') }}
      </h4>
      <span>{{ $t('validation.requiredPermissions', { 'permissions': 'System Admin' }) }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, reactive, ref, computed, watch } from 'vue';
import { RouterLink, useRouter, type RouteLocationRaw } from 'vue-router';
import { storeToRefs } from 'pinia';
import { toast } from 'vue3-toastify';
import { Modal } from 'bootstrap';
import api from '@/services/api';
import { useApiRequestLogsStore, useSessionStore, useSitesStore, useUsersStore } from '@/stores';
import type { IApiRequestLogFilter } from '@/stores/apiRequestLogs.store';
import RelativeDateTime from '@/components/RelativeDateTime.vue';
import TypeaheadComponent from '@/components/TypeaheadComponent.vue';
import TranslationsService from '@/services/translations.ts';
import User from '@/models/frontend/User';

type EntityScope = 'site' | 'tenant';

type EntityRouteConfig = {
  name: string;
  paramName: string;
  scope: EntityScope;
};

type EntityPathMatch = {
  prefix: string;
  subject: string;
  id: number;
  rest: string;
  config: EntityRouteConfig;
  to: RouteLocationRaw;
};

const ENTITY_ROUTES: Record<string, EntityRouteConfig> = {
  deliveries: { name: 'delivery', paramName: 'deliveryId', scope: 'site' },
  orders: { name: 'order', paramName: 'orderId', scope: 'site' },
  items: { name: 'item', paramName: 'itemId', scope: 'tenant' },
  suppliers: { name: 'supplier', paramName: 'supplierId', scope: 'tenant' },
  consumptions: { name: 'consumption', paramName: 'consumptionId', scope: 'site' },
  'item-blocks': { name: 'itemblock', paramName: 'itemBlockId', scope: 'site' },
  'label-printers': { name: 'label-printer', paramName: 'printerId', scope: 'site' },
  'label-templates': { name: 'label-template', paramName: 'templateId', scope: 'tenant' },
};

const ENTITY_PATH_REGEX = /^(\/api\/v\d+\.\d+\/)([^/]+)\/(\d+)(\/.*)?$/;

function parseEntityPath(path: string): EntityPathMatch | null {
  const match = ENTITY_PATH_REGEX.exec(path);
  if (!match) return null;
  const basePrefix = match[1]!;
  const subject = match[2]!;
  const idStr = match[3]!;
  const rest = match[4];
  const config = ENTITY_ROUTES[subject];
  if (!config) return null;
  const id = Number.parseInt(idStr, 10);
  return {
    prefix: `${basePrefix}${subject}/`,
    subject,
    id,
    rest: rest ?? '',
    config,
    to: TranslationsService.i18nRoute({ name: config.name, params: { [config.paramName]: id } }),
  };
}

const sessionStore = useSessionStore();
const { isSystemAdmin, selectedSiteId, selectedTenantId, userSites } = storeToRefs(sessionStore);
const router = useRouter();

const usersStore = useUsersStore();
const { users: availableUsers } = storeToRefs(usersStore);

const sitesStore = useSitesStore();

function extractSiteIdFromQuery(qs: string | null | undefined): number | null {
  if (!qs) return null;
  const search = qs.startsWith('?') ? qs.slice(1) : qs;
  try {
    const value = new URLSearchParams(search).get('siteId');
    if (!value) return null;
    const n = Number.parseInt(value, 10);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function siteHintFromQuery(qs: string | null | undefined): { id: number; name: string } | null {
  const id = extractSiteIdFromQuery(qs);
  if (id == null) return null;
  const site = sitesStore.getById(id);
  if (!site) return null;
  return { id, name: site.name };
}

const store = useApiRequestLogsStore();
const { page, detail, loading, detailLoading, error } = storeToRefs(store);

const selectedUser = ref<User | null>(null);

const userProjection = (user: User | null): string => {
  if (!user) return '';
  return user.name ? `${user.name} <${user.email}>` : user.email;
};

const onUserSelected = (user: User | null) => {
  selectedUser.value = user;
  filter.userId = user ? user.id : null;
};

const clearUser = () => onUserSelected(null);

const userLabel = (id: number): string => {
  const user = usersStore.getUserById(id);
  if (!user) return `User #${id}`;
  return user.name ? `${user.name} <${user.email}>` : user.email;
};

const navigatingPath = ref<string | null>(null);

async function navigateToEntity(parsed: EntityPathMatch, originPath: string) {
  if (navigatingPath.value) return;
  navigatingPath.value = originPath;
  try {
    const response = await api.get<{ siteId?: number; tenantId?: number }>(`v1.0/${parsed.subject}/${parsed.id}`);
    const entity = response.data ?? {};

    if (parsed.config.scope === 'site' && typeof entity.siteId === 'number') {
      if (selectedSiteId.value !== entity.siteId) {
        const accessible = userSites.value.some(s => s.id === entity.siteId);
        if (!accessible) {
          toast.warning(`Entity belongs to site #${entity.siteId} which is not accessible to you.`, { autoClose: 4000 });
          return;
        }
        await sessionStore.selectSite(entity.siteId);
      }
    } else if (parsed.config.scope === 'tenant' && typeof entity.tenantId === 'number') {
      if (selectedTenantId.value !== entity.tenantId) {
        await sessionStore.selectTenant(entity.tenantId);
      }
      if (selectedSiteId.value === 0) {
        const firstSite = userSites.value.find(s => s.tenant.id === entity.tenantId);
        if (firstSite) await sessionStore.selectSite(firstSite.id);
      }
    }

    // Close detail modal if it's open before navigating
    detailModal?.hide();

    await router.push(parsed.to);
  } catch (e: any) {
    if (e?.response?.status === 404) {
      toast.error(`${parsed.subject} #${parsed.id} no longer exists.`, { autoClose: 4000 });
    } else {
      toast.error(`Could not resolve ${parsed.subject} #${parsed.id}.`, { autoClose: 4000 });
    }
  } finally {
    navigatingPath.value = null;
  }
}

const take = ref(50);
const skip = ref(0);

const filter = reactive({
  method: null as string | null,
  path: '' as string,
  statusCode: null as number | null,
  minStatusCode: null as number | null,
  maxStatusCode: null as number | null,
  userId: null as number | null,
  from: '' as string,
  to: '' as string,
  minResponseTimeMs: null as number | null,
});

const detailModalRef = ref<HTMLElement | null>(null);
let detailModal: Modal | null = null;

function numOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

const buildFilter = computed<IApiRequestLogFilter>(() => ({
  method: filter.method || null,
  path: filter.path || null,
  statusCode: numOrNull(filter.statusCode),
  minStatusCode: numOrNull(filter.minStatusCode),
  maxStatusCode: numOrNull(filter.maxStatusCode),
  userId: numOrNull(filter.userId),
  from: filter.from ? new Date(filter.from).toISOString() : null,
  to: filter.to ? new Date(filter.to).toISOString() : null,
  minResponseTimeMs: numOrNull(filter.minResponseTimeMs),
  skip: skip.value,
  take: take.value,
}));

async function load() {
  await store.search(buildFilter.value);
}

function applyFilter() {
  skip.value = 0;
  load();
}

function resetFilter() {
  filter.method = null;
  filter.path = '';
  filter.statusCode = null;
  filter.minStatusCode = null;
  filter.maxStatusCode = null;
  filter.userId = null;
  selectedUser.value = null;
  filter.from = '';
  filter.to = '';
  filter.minResponseTimeMs = null;
  skip.value = 0;
  load();
}

function nextPage() {
  if (skip.value + take.value >= page.value.total) return;
  skip.value += take.value;
  load();
}

function prevPage() {
  if (skip.value === 0) return;
  skip.value = Math.max(0, skip.value - take.value);
  load();
}

watch(take, () => {
  skip.value = 0;
  load();
});

async function select(id: number) {
  if (!detailModal && detailModalRef.value) {
    detailModal = new Modal(detailModalRef.value);
  }
  detailModal?.show();
  await store.loadDetail(id);
}

function methodBadge(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET': return 'text-bg-primary';
    case 'POST': return 'text-bg-success';
    case 'PUT': return 'text-bg-warning';
    case 'PATCH': return 'text-bg-info';
    case 'DELETE': return 'text-bg-danger';
    default: return 'text-bg-secondary';
  }
}

function statusBadge(status: number): string {
  if (status >= 500) return 'text-bg-danger';
  if (status >= 400) return 'text-bg-warning';
  if (status >= 300) return 'text-bg-info';
  if (status >= 200) return 'text-bg-success';
  return 'text-bg-secondary';
}

function rowClass(status: number): string {
  if (status >= 500) return 'table-danger';
  if (status >= 400) return 'table-warning';
  return '';
}

function formatJson(value: string | null): string {
  if (!value) return '—';
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

onMounted(() => {
  if (isSystemAdmin.value) {
    usersStore.fetchAll();
    sitesStore.fetchAll();
    load();
  }
});
</script>

<style scoped>
pre {
  max-height: 320px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
